import React from 'react';
import './SubcategoryPage.css';

// ── SubcategoryPage ───────────────────────────────────────────────────────────
// Third level of progressive disclosure: shows products within a subcategory.
// Receives products already filtered + deduplicated (one card per base productId).
//
// Props:
//   subcategory    — { id, name, icon }
//   aisle          — { id, name, icon } — for back button label
//   products       — filtered, deduplicated products for this subcategory
//   onSelectProduct — called when a product row is tapped
//   onAddToBasket  — called with (product, prices) from quick-add
//   onBack         — back to aisle page

// UI-only: removes the subcategory word from product names where it's redundant.
//   Leading word  ("Milk Whole")            → moves it to the end  → "Whole Milk"
//   Middle word   ("Cheddar Cheese Block")  → strips it            → "Cheddar Block"
//   Trailing word ("Cottage Cheese")        → strips it            → "Cottage"
// The raw product.name in the database is never modified.
function formatProductName(name, subcategoryName) {
  if (!name || !subcategoryName) return name;
  // Use the first word of the subcategory name as the keyword to match.
  const keyword = subcategoryName.split(/\s+/)[0];
  const atStart = new RegExp(`^${keyword}\\b\\s*`, 'i');
  const anywhere = new RegExp(`\\s*\\b${keyword}\\b`, 'i');
  if (atStart.test(name)) {
    // Strip the leading keyword and re-append it at the end.
    return name.replace(atStart, '').trim() + ' ' + keyword;
  }
  // Mid- or trailing keyword — remove it and tidy up any double spaces.
  return name.replace(anywhere, '').replace(/\s{2,}/g, ' ').trim();
}

function SubcategoryPage({ subcategory, aisle, products, onSelectProduct, onAddToBasket, onBack, stores = [] }) {
  const sorted = [...products].sort((a, b) => a.name.localeCompare(b.name));

  const getCheapestPrice = (product) => {
    const valid = product.prices?.filter(p => p.price > 0);
    if (!valid?.length) return null;
    return Math.min(...valid.map(p => p.price));
  };

  // Returns the name of the store with the cheapest price for a product.
  const getCheapestStore = (product) => {
    if (!product.prices?.length || !stores.length) return null;
    const valid = product.prices.filter(p => p.price > 0);
    if (!valid.length) return null;
    const best = valid.reduce((min, p) => p.price < min.price ? p : min, valid[0]);
    const normId = (best.storeId || '').replace(/^store_/, '');
    return stores.find(s => s.id === normId || s.id === best.storeId)?.name || null;
  };

  const handleAdd = (e, product) => {
    e.stopPropagation();
    if (onAddToBasket) onAddToBasket(product, product.prices || []);
  };

  return (
    <div className="subcat-page">

      {/* ── Nav row: icon back + breadcrumb ── */}
      <div className="subcat-nav-row">
        <button className="subcat-back-btn" onClick={onBack} aria-label={`Back to ${aisle.name}`}>
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="13 18 7 12 13 6"/>
          </svg>
        </button>
        <nav className="subcat-breadcrumb" aria-label="Navigation">
          <button className="subcat-crumb-link" onClick={onBack}>
            {aisle.name}
          </button>
          <span className="subcat-crumb-sep" aria-hidden="true">›</span>
          <span className="subcat-crumb-current">{subcategory.name}</span>
        </nav>
      </div>

      {/* Subcategory header */}
      <div className="subcat-header">
        <span className="subcat-header-icon" aria-hidden="true">{subcategory.icon}</span>
        <h1 className="subcat-title">{subcategory.name}</h1>
      </div>

      <p className="subcat-hint">Next step: add items, then compare stores →</p>

      {/* Product list */}
      <div className="subcat-list">
        {sorted.length === 0 ? (
          <div className="subcat-empty">No products found.</div>
        ) : sorted.map(product => {
          const cheapest          = getCheapestPrice(product);
          const cheapestStoreName = getCheapestStore(product);
          const tierClass         = product.ownBrand && product.tier ? product.tier : null;
          const displayName       = formatProductName(product.name, subcategory.name);

          return (
            <div
              key={product.id}
              className="subcat-row"
              role="button"
              tabIndex={0}
              onClick={() => onSelectProduct(product)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectProduct(product);
                }
              }}
              aria-label={`Compare prices for ${displayName}`}
            >
              <div className="subcat-row-info">
                <div className="subcat-row-name">{displayName}</div>
                <div className="subcat-row-meta">
                  {product.size && <span>{product.size}</span>}
                  {tierClass && (
                    <span className={`val-badge ${tierClass}`}>
                      {tierClass === 'basic'
                        ? 'Basic'
                        : tierClass.charAt(0).toUpperCase() + tierClass.slice(1)}
                    </span>
                  )}
                </div>
              </div>

              {cheapest !== null && (
                <div className="subcat-price-col">
                  <span className="subcat-from">From £{cheapest.toFixed(2)}</span>
                  {cheapestStoreName && (
                    <span className="subcat-cheapest-store">at {cheapestStoreName}</span>
                  )}
                </div>
              )}

              <button
                className="subcat-add-btn"
                onClick={(e) => handleAdd(e, product)}
                aria-label={`Add ${displayName} to list`}
              >
                +
              </button>
            </div>
          );
        })}
      </div>

      {/* Coverage note */}
      {sorted.length > 0 && (
        <p className="subcat-coverage-note">
          Prices improve as more products are added.
        </p>
      )}

    </div>
  );
}

export default SubcategoryPage;
